import React, { useEffect, useState, useRef, useImperativeHandle } from "react";
import { Grid } from "@mui/material";
import { useHistory } from "react-router";
import { Box, Button, Card, DateField, Form, Typography } from "@curves/ui-base";
import { EmployeeSelect } from "@icoach/components/";
import { CheckoutOrderCart, CheckoutPaidListBox, SetTargetSearchBar, CreditOrderList, QuestionnaireBox } from "@icoach/accountBook/components";
import useOrderCheckoutApi from "@apis/useOrderCheckoutApi";
import { refIsRequiredError, isEmpty, isEqual } from "@util/utils";
import { getToday } from "@util/moment";
import useMessageDialog from "@util/hook/useMessageDialog";
import { ACCOUNT_BOOK_ORDERS_OVERVIEW_URL } from "@icoach/router/routerPath";
import { checkoutDateMessageConfig, isBeforeOfCurrentMonth } from "@icoach/accountBook/staticData";
import useInvoiceApi from "@apis/useInvoiceApi";
import { REVENUE_TYPE } from "@icoach/constants/dataEnums";
import { InvoiceByCarrierNo, InvoiceByCarrierType, InvoiceByDonate, InvoiceByEmail, InvoiceByIssuingType, InvoiceByManual, InvoiceByTriplicateUniform } from "@icoach/components/invoice";
import useValidationErrors from "@util/hook/useValidationErrors";
import { ORDER_INVOICE_ISSUING_TYPE, INVOICE_CARRIER_TYPE } from "@icoach/constants/dataEnums";
import { CheckoutSmartFitnessBandFee } from "@icoach/accountBook/components/CheckoutSmartFitnessBandFee"; //智能月費區塊
// 設定建單日與建單人
export const CreateOrderInfoSection = React.forwardRef((props, ref) => {
	const { className, resource = {} } = props;
	const { coachOptions: employeeOptions = [] } = resource;
	const setMessageDialog = useMessageDialog();
	const employeeRef = useRef(null);
	const orderDateRef = useRef(null);
	const today = getToday();

	const handleChangeDate = (d, v) => {
		if (isBeforeOfCurrentMonth(v)) {
			setMessageDialog(checkoutDateMessageConfig);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				let result = {};
				if (employeeRef.current && employeeRef.current.getResult) result = Object.assign(result, { employeeID: employeeRef.current.getResult() });
				if (orderDateRef.current && orderDateRef.current.getResult) result = Object.assign(result, { orderDate: orderDateRef.current.getResult() });
				return result;
			},
			isError: () => {
				let isError = false;
				// 檢核建立日期是否在當月之前
				if (orderDateRef.current && orderDateRef.current.getResult) {
					let orderDate = orderDateRef.current.getResult();
					if (!isError) {
						isError = isBeforeOfCurrentMonth(orderDate);
						if (isError) setMessageDialog(checkoutDateMessageConfig);
					}
				}

				if (!isError) {
					isError = refIsRequiredError(employeeRef, orderDateRef);
				}
				return isError;
			},
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<Box className={className}>
			<Grid spacing={3} container>
				<Grid xs={3} item>
					<EmployeeSelect ref={employeeRef} options={employeeOptions} label="擔當" fullWidth required />
				</Grid>
				<Grid xs={3} item>
					<DateField label="建立日期" ref={orderDateRef} defaultValue={today} onChange={handleChangeDate} fullWidth />
				</Grid>
			</Grid>
		</Box>
	);
});

export const InvoiceCard = React.forwardRef((props, ref) => {
	const { className, sourceData, options: optionsProps = {}, errorModule = {}, readOnly } = props;
	const { getInvoiceOptionsApi } = useInvoiceApi();
	const { issuingType: issuingTypeProps, invoiceCarrierType: invoiceCarrierTypeProps, invoiceCarrierNo, donationCode, invoiceNo, issuingEmail } = sourceData || {};
	const [options, setOptions] = useState(optionsProps);
	const { invoiceTypeOptions = [], invoiceCarrierTypeOptions = [] } = options;
	const [issuingType, setIssuingType] = useState(issuingTypeProps || 2);
	const [invoiceCarrierType, setInvoiceCarrierType] = useState(invoiceCarrierTypeProps || 1);
	const issuingTypeRef = useRef(null);
	const donateRef = useRef(null);
	const invoiceCarrierTypeRef = useRef(null);
	const invoiceCarrierNoRef = useRef(null);
	const triplicateUniformRef = useRef(null);
	const manualRef = useRef(null);
	const emailRef = useRef(null);

	const initOptionsApi = async () => {
		const resp = await getInvoiceOptionsApi();
		if (resp) {
			setOptions(resp);
		}
	};

	const handleChangeIssuingType = value => {
		setIssuingType(value);
	};

	const handleChangeInvoiceCarrierType = value => {
		setInvoiceCarrierType(value);
	};

	const getResult = () => {
		let result = {};

		if (issuingTypeRef.current && issuingTypeRef.current.getResult) {
			result = Object.assign(result, issuingTypeRef.current.getResult());
		}

		if (donateRef.current && donateRef.current.getResult) {
			result = Object.assign(result, donateRef.current.getResult());
		}

		if (invoiceCarrierTypeRef.current && invoiceCarrierTypeRef.current.getResult) {
			const { carrierType } = invoiceCarrierTypeRef.current.getResult();
			Object.assign(result, { invoiceCarrierType: carrierType });
		}

		if (invoiceCarrierNoRef.current && invoiceCarrierNoRef.current.getResult) {
			const { carrierNo } = invoiceCarrierNoRef.current.getResult();
			Object.assign(result, { invoiceCarrierNo: carrierNo });
		}

		if (triplicateUniformRef.current && triplicateUniformRef.current.getResult) {
			result = Object.assign(result, triplicateUniformRef.current.getResult());
		}

		if (manualRef.current && manualRef.current.getResult) {
			result = Object.assign(result, manualRef.current.getResult());
		}

		if (emailRef.current && emailRef.current.getResult) {
			result = Object.assign(result, emailRef.current.getResult());
		}

		return result;
	};

	useEffect(
		() => {
			if (isEmpty(options)) {
				initOptionsApi();
			}
		},
		// eslint-disable-next-line
		[optionsProps],
	);

	useEffect(
		() => {
			if (issuingTypeProps) setIssuingType(issuingTypeProps);
		},
		// eslint-disable-next-line
		[issuingTypeProps],
	);

	useEffect(
		() => {
			if (invoiceCarrierTypeProps) setInvoiceCarrierType(invoiceCarrierTypeProps);
		},
		// eslint-disable-next-line
		[invoiceCarrierTypeProps],
	);

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => refIsRequiredError(issuingTypeRef, donateRef, invoiceCarrierTypeRef, invoiceCarrierNoRef, triplicateUniformRef, manualRef, emailRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<Card className={className}>
			<Card.CardContent className="p-4">
				<Grid className={className} spacing={2} container>
					<Grid xs={12} item>
						<InvoiceByIssuingType
							ref={issuingTypeRef}
							issuingType={issuingTypeProps}
							options={invoiceTypeOptions}
							errorData={errorModule}
							onChange={handleChangeIssuingType}
							readOnly={readOnly}
						/>
					</Grid>
					{isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.DONATE) && (
						<Grid xs={12} item>
							<InvoiceByDonate ref={donateRef} donationCode={donationCode} errorData={errorModule} readOnly={readOnly} />
						</Grid>
					)}
					{isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE) && (
						<React.Fragment>
							<Grid xs={12} item>
								<InvoiceByCarrierType
									ref={invoiceCarrierTypeRef}
									carrierType={invoiceCarrierType}
									options={invoiceCarrierTypeOptions}
									errorData={errorModule}
									onChange={handleChangeInvoiceCarrierType}
									readOnly={readOnly}
								/>
							</Grid>
							{(isEqual(invoiceCarrierType, INVOICE_CARRIER_TYPE.CITIZENDIGITALCERTIFICATE) || isEqual(invoiceCarrierType, INVOICE_CARRIER_TYPE.MOBILEBARCODE)) && (
								<Grid xs={12} item>
									<InvoiceByCarrierNo ref={invoiceCarrierNoRef} carrierNo={invoiceCarrierNo} carrierType={invoiceCarrierType} errorData={errorModule} readOnly={readOnly} />
								</Grid>
							)}
						</React.Fragment>
					)}
					{isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.TRIPLICATEUNIFORMINVOICE) && (
						<InvoiceByTriplicateUniform ref={triplicateUniformRef} taxID={sourceData?.taxID} taxTitle={sourceData?.taxTitle} errorData={errorModule} readOnly={readOnly} />
					)}
					{isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.MANUAL) && (
						<Grid xs={12} item>
							<InvoiceByManual ref={manualRef} invoiceNo={invoiceNo} errorData={errorModule} readOnly={readOnly} />
						</Grid>
					)}
					{!isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.MANUAL) && (
						<Grid item xs={12}>
							<InvoiceByEmail
								ref={emailRef}
								issuingEmail={issuingEmail}
								InputProps={{ placeholder: "必須填寫，以便收取重要權益異動、發票、優惠等相關訊息" }}
								errorData={errorModule}
								readOnly={readOnly}
							/>
						</Grid>
					)}
				</Grid>
			</Card.CardContent>
		</Card>
	);
});

// 判斷是否收取智能月費center
export const CheckOutContent = React.forwardRef((props, ref) => {
	const { className, resource, getReceiver, errorModule } = props;
	const { postOrdersCheckoutCheckDeviceOrderApi } = useOrderCheckoutApi();
	const [orderTotal, setOrderTotal] = useState(0);
	const [paidPrice, setPaidPrice] = useState(0);
	const [isShowQuestionnaireBox, setShowQuestionnaireBox] = useState(false);
	const [creditOrders, setCreditOrder] = useState({});
	// 判斷是否訂閱智能系統
	const [checkUsingDeviceOrder, setCheckUsingDeviceOrder] = useState(null);
	const { orders, invoiceSetting } = creditOrders;
	const [isShowCreditOrders, setShowCreditOrders] = useState(false);
	const checkoutCartRef = useRef(null);
	const creditOrderRef = useRef(null);
	const checkoutRef = useRef(null);
	const questionnaireRef = useRef(null);
	const invoiceRef = useRef(null);
	// 智能月費區塊API
	const [smartFitnessBandFeeData, setSmartFitnessBandFeeData] = useState(null);
	const [isShowSmartFitnessBandFee, setIsShowSmartFitnessBandFee] = useState(false);
	const smartFitnessBandFeeRef = useRef(null);
	const deviceOrderItemsRef = useRef([]);

	const handleOnPriceChange = (price, itemData) => {
		checkSmartFitnessBandFeeApi(price, itemData);
	};

	// 處理刪除既存月費時，同步移除對應的智能月費
	const handleOnItemDelete = itemData => {
		// 檢查是否為既存月費 (revenueType === "201")
		const isExistingMonthlyFee = isEqual(itemData.revenueType, REVENUE_TYPE.MONTHLYFEE);

		if (isExistingMonthlyFee && checkUsingDeviceOrder) {
			// 從 deviceOrderItemsRef 中移除對應項目
			deviceOrderItemsRef.current = deviceOrderItemsRef.current.filter(item => !isEqual(item.name, itemData.name));

			// 重新檢查智能月費
			refreshSmartFitnessBandFeeApi();
		}
	};

	// 重新檢查智能月費（用於刪除後更新）
	const refreshSmartFitnessBandFeeApi = async () => {
		// 如果會員沒有訂閱智能系統，直接隱藏智能月費區塊，不呼叫 API
		if (!checkUsingDeviceOrder) {
			setIsShowSmartFitnessBandFee(false);
			deviceOrderItemsRef.current = [];
			return;
		}

		const memberID = creditOrders?.member?.memberID;

		// 如果沒有既存月費項目，直接隱藏智能月費區塊，不呼叫 API
		if (isEmpty(deviceOrderItemsRef.current)) {
			setIsShowSmartFitnessBandFee(false);
			return;
		}

		// 呼叫 API
		const response = await postOrdersCheckoutCheckDeviceOrderApi(memberID, deviceOrderItemsRef.current);

		if (!isEmpty(response)) {
			setSmartFitnessBandFeeData(response);
			setIsShowSmartFitnessBandFee(true);
		} else {
			setIsShowSmartFitnessBandFee(false);
		}
	};

	// 呼叫API取得是否收取智能月費
	const checkSmartFitnessBandFeeApi = async (price, itemData) => {
		try {
			// 如果 itemData 不存在，說明是從 ProductItemRow 調用的，直接返回
			if (!itemData || !itemData.revenueType) {
				return;
			}

			// 如果會員沒有訂閱智能系統，直接返回，不呼叫 API
			if (!checkUsingDeviceOrder) {
				setIsShowSmartFitnessBandFee(false);
				return;
			}

			// 取得會員 ID
			const memberID = creditOrders?.member?.memberID;

			deviceOrderItemsRef.current.push({
				revenueType: itemData.revenueType,
				name: itemData.name,
				assignPrice: price,
				notes: itemData.notes || "",
			});

			// 呼叫 API
			const response = await postOrdersCheckoutCheckDeviceOrderApi(memberID, deviceOrderItemsRef.current);

			if (response?.length) {
				// 過濾出需要收取的智能月費（assignPrice > 0）
				const validSmartFees = response.filter(item => item.assignPrice > 0);

				if (validSmartFees.length > 0) {
					setSmartFitnessBandFeeData(validSmartFees);
					setIsShowSmartFitnessBandFee(true);
				} else {
					setIsShowSmartFitnessBandFee(false);
				}
			} else {
				setIsShowSmartFitnessBandFee(false);
			}
		} catch (error) {
			setIsShowSmartFitnessBandFee(false);
		}
	};

	let calculateOrderTotal = () => {
		let total = 0;
		if (checkoutCartRef && checkoutCartRef.current && checkoutCartRef.current.getTotal) {
			let checkoutCartTotal = checkoutCartRef.current.getTotal();
			total += checkoutCartTotal;
		}

		if (creditOrderRef && creditOrderRef.current && creditOrderRef.current.getTotalData) {
			let data = creditOrderRef.current.getTotalData();

			total += data.total;
			setPaidPrice(data.paidPrice);
		}

		setOrderTotal(total);
	};

	// 監聽 creditOrders 變化，更新會員訂閱狀態
	useEffect(() => {
		const isUsingDevice = creditOrders?.checkUsingDeviceOrder;
		setCheckUsingDeviceOrder(isUsingDevice);

		// 如果會員沒有訂閱智能系統，清除相關資料並隱藏智能月費區塊
		if (!isUsingDevice) {
			deviceOrderItemsRef.current = [];
			setIsShowSmartFitnessBandFee(false);
		}
	}, [creditOrders]);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				let result = {};
				if (checkoutCartRef.current && checkoutCartRef.current.getResult) result = Object.assign(result, checkoutCartRef.current.getResult());
				if (creditOrderRef.current && creditOrderRef.current.getResult) {
					result = Object.assign(result, creditOrderRef.current.getResult());
				} else {
					result.checkoutOrderIDs = [];
				}
				if (checkoutRef.current && checkoutRef.current.getResult) result = Object.assign(result, checkoutRef.current.getResult());
				if (questionnaireRef.current && questionnaireRef.current.getResult) result = Object.assign(result, questionnaireRef.current.getResult());
				if (invoiceRef.current && checkoutRef.current.getResult) result = Object.assign(result, { invoiceSetting: invoiceRef.current.getResult() });
				// 呼叫API取得是否收取智能月費
				if (smartFitnessBandFeeRef.current && smartFitnessBandFeeRef.current.getResult) result = Object.assign(result, smartFitnessBandFeeRef.current.getResult());
				return result;
			},
			isError: () => {
				// 基本必填檢查
				let isError = refIsRequiredError(checkoutCartRef, creditOrderRef, checkoutRef, questionnaireRef, invoiceRef);

				// 如果顯示智能月費區塊，也要檢查 checkbox 是否勾選
				if (!isError && isShowSmartFitnessBandFee && smartFitnessBandFeeRef.current) {
					isError = refIsRequiredError(smartFitnessBandFeeRef);
				}

				return isError;
			},
			setCreditOrder,
			setShowCreditOrders,
		}),
		// eslint-disable-next-line
		[isShowSmartFitnessBandFee],
	);

	return (
		<Box className={className}>
			<Grid spacing={3} container>
				{isShowCreditOrders && (
					<Grid xs={12} item>
						<CreditOrderList ref={creditOrderRef} orders={orders} updateOrderTotal={calculateOrderTotal} />
					</Grid>
				)}
				<Grid xs={12} item>
					<CheckoutOrderCart
						className="mb-1"
						ref={checkoutCartRef}
						resource={resource}
						getReceiver={getReceiver}
						setShowQuestionnaireBox={setShowQuestionnaireBox}
						updateOrderTotal={calculateOrderTotal}
						onPriceChange={handleOnPriceChange}
						onMonthlyFeeDelete={handleOnItemDelete}
						checkUsingDeviceOrder={checkUsingDeviceOrder}
					/>
				</Grid>
				{/* 智能月費區塊 */}
				{isShowSmartFitnessBandFee && (
					<Grid xs={12} item>
						<CheckoutSmartFitnessBandFee ref={smartFitnessBandFeeRef} sourceData={smartFitnessBandFeeData} />
					</Grid>
				)}
				<Grid xs={6} item>
					<InvoiceCard ref={invoiceRef} sourceData={{ ...invoiceSetting, issuingEmail: creditOrders.member?.issuingEmail || "" }} options={resource} showEmail />
				</Grid>
				<Grid xs={6} item>
					{isShowQuestionnaireBox && <QuestionnaireBox ref={questionnaireRef} className="mb-4" resource={resource} />}
					<CheckoutPaidListBox ref={checkoutRef} receivableAmount={orderTotal} paidPrice={paidPrice} resource={resource} errorModule={errorModule} />
				</Grid>
			</Grid>
		</Box>
	);
});

// 新增收入
// 增加智能月費
const AccountBookCreateOrderPage = () => {
	const { getOrderCheckoutOptionsApi, getOrderCheckoutApi, postOrderCheckoutByPeopleApi } = useOrderCheckoutApi();
	const history = useHistory();
	const setMessageDialog = useMessageDialog();
	const [resource, setResource] = useState({});
	const [isShowContent, setShowContent] = useState(false);
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();
	const orderInfoRef = useRef(null);
	const searchBarRef = useRef(null);
	const checkoutRef = useRef(null);

	// 打包結帳 增加智能月費API欄位
	const getParams = () => {
		let params = {};
		if (orderInfoRef.current && orderInfoRef.current.getResult) {
			params = Object.assign(params, orderInfoRef.current.getResult());
		}
		if (searchBarRef.current && searchBarRef.current.getResult) {
			params = Object.assign(params, searchBarRef.current.getResult());
		}
		// 打包結帳 增加智能月費API欄位
		if (checkoutRef.current && checkoutRef.current.getResult) {
			params = Object.assign(params, checkoutRef.current.getResult());
		}
		return params;
	};

	const checkError = () => {
		return refIsRequiredError(orderInfoRef, searchBarRef, checkoutRef);
	};

	const handleClickCheckout = () => {
		clearValidationErrors();
		let isError = checkError();

		if (!isError) {
			let params = getParams();
			doOrderCheckoutApi(params);
		}
	};

	const handleCancel = () => {
		history.push(ACCOUNT_BOOK_ORDERS_OVERVIEW_URL);
	};

	const doOptionsApi = async () => {
		let resp = await getOrderCheckoutOptionsApi();
		if (resp) {
			setResource(resp);
		}
	};

	// 會員訂單確認
	const doConfirmMemberOrderApi = async memberID => {
		let resp = await getOrderCheckoutApi(memberID);
		if (resp) {
			if (checkoutRef.current && checkoutRef.current.setCreditOrder) {
				checkoutRef.current.setCreditOrder(resp);
			}
			if (!isEmpty(resp.orders)) {
				const handleClickConfirm = checkoutRef => {
					if (checkoutRef.current && checkoutRef.current.setShowCreditOrders) checkoutRef.current.setShowCreditOrders(true);
				};
				setMessageDialog({
					open: true,
					title: "欠費提醒",
					msg: "查詢到會員尚有未結清的訂單，請問是否於本次消費一起結帳？",
					onConfirm: () => handleClickConfirm(checkoutRef),
				});
			}
		}
	};

	// 新增收入api
	const doOrderCheckoutApi = async params => {
		const resp = await postOrderCheckoutByPeopleApi(params);
		const { isError = false, result } = resp || {};

		if (isError) {
			// 0002 錯誤
			updateValidationErrors(result);
		} else if (isEmpty(resp)) {
			// 失敗
		} else {
			// 成功
			history.push(ACCOUNT_BOOK_ORDERS_OVERVIEW_URL);
		}
	};

	useEffect(
		() => {
			doOptionsApi();
		},
		// eslint-disable-next-line
		[],
	);

	return (
		<Box className="container main-container-spacing">
			<Form onSubmit={handleClickCheckout}>
				<Box component="header" className="page-header">
					<Typography className="page-header-title" variant="h3">
						新增收入
					</Typography>
				</Box>
				<CreateOrderInfoSection className="mb-2" ref={orderInfoRef} resource={resource} />
				{/* 呼叫是否訂閱智能月費API */}
				<SetTargetSearchBar
					className="mb-2"
					title="設定購買人"
					ref={searchBarRef}
					resource={resource}
					isShowContent={isShowContent}
					setShowContent={setShowContent}
					doConfirmMemberOrderApi={doConfirmMemberOrderApi}
				/>
				{isShowContent && (
					<React.Fragment>
						<CheckOutContent ref={checkoutRef} resource={resource} getReceiver={searchBarRef.current.getReceiver} errorModule={validationErrors} isShowCash isShoCredit />
						<Grid spacing={3} container>
							<Grid xs={12} item>
								<Box className="btn-group text-right mt-4">
									<Button variant="outlined" onClick={handleCancel}>
										取消，回列表
									</Button>
									<Button type="submit" variant="contained">
										確認結帳
									</Button>
								</Box>
							</Grid>
						</Grid>
					</React.Fragment>
				)}
			</Form>
		</Box>
	);
};

export default AccountBookCreateOrderPage;
